import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Paper, Box, IconButton } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

// Sortable Item Component
const SortableItem = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      elevation={isDragging ? 3 : 1}
      sx={{
        mb: 2,
        p: 2,
        border: isDragging ? '2px dashed #1976d2' : '1px solid #e0e0e0',
        backgroundColor: isDragging ? '#f5f5f5' : 'white',
      }}
    >
      <Box display="flex" alignItems="flex-start" gap={1}>
        <IconButton
          {...attributes}
          {...listeners}
          size="small"
          sx={{
            cursor: 'grab',
            '&:active': { cursor: 'grabbing' },
            color: '#666',
            mt: 0.5,
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <Box flex={1}>
          {children}
        </Box>
      </Box>
    </Paper>
  );
};

// Main Drag Drop Section Component
const DragDropSection = ({ items, onReorder, renderItem, idField = 'id' }) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      // Find indices based on the generated _dragId
      const oldIndex = itemsWithIds.findIndex(item => item._dragId === active.id);
      const newIndex = itemsWithIds.findIndex(item => item._dragId === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newItems = arrayMove(items, oldIndex, newIndex);
        onReorder(newItems);
      }
    }
  };

  // Generate unique IDs for items that don't have them
  const itemsWithIds = items.map((item, index) => ({
    ...item,
    _dragId: item[idField] || `item-${index}`,
  }));

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={itemsWithIds.map(item => item._dragId)}
        strategy={verticalListSortingStrategy}
      >
        {itemsWithIds.map((item, index) => (
          <SortableItem key={item._dragId} id={item._dragId}>
            {renderItem(item, index)}
          </SortableItem>
        ))}
      </SortableContext>
    </DndContext>
  );
};

export default DragDropSection;
